import { useCallback, useEffect, useRef } from "react";
import useBDocTranslator from "../../hooks/useBDocTranslator";

/**
 * Hook personalizado para mejorar la accesibilidad del módulo dental
 * Proporciona navegación por teclado, lectores de pantalla y ARIA labels
 */
export default function useDentalAccessibility() {
  const { t } = useBDocTranslator();
  const announcementRef = useRef(null);
  const focusHistory = useRef([]);

  // Crear región de anuncios para lectores de pantalla
  useEffect(() => {
    if (!announcementRef.current) {
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", "polite");
      announcement.setAttribute("aria-atomic", "true");
      announcement.setAttribute("class", "sr-only");
      announcement.style.position = "absolute";
      announcement.style.left = "-10000px";
      announcement.style.width = "1px";
      announcement.style.height = "1px";
      announcement.style.overflow = "hidden";
      document.body.appendChild(announcement);
      announcementRef.current = announcement;
    }

    return () => {
      if (announcementRef.current) {
        document.body.removeChild(announcementRef.current);
        announcementRef.current = null;
      }
    };
  }, []);

  // Anunciar cambios para lectores de pantalla
  const announce = useCallback((message, priority = "polite") => {
    if (announcementRef.current) {
      announcementRef.current.setAttribute("aria-live", priority);
      announcementRef.current.textContent = message;

      // Limpiar después de un tiempo para evitar spam
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = "";
        }
      }, 1000);
    }
  }, []);

  // Navegación por teclado para dientes
  const handleToothKeyNavigation = useCallback(
    (event, toothNumber, onActivate) => {
      const { key, shiftKey } = event;

      switch (key) {
        case "Enter":
        case " ":
          event.preventDefault();
          onActivate();
          announce(t(`Tooth ${toothNumber} activated`));
          break;

        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          focusNextTooth(toothNumber, !shiftKey);
          break;

        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          focusPreviousTooth(toothNumber, !shiftKey);
          break;

        case "Home":
          event.preventDefault();
          focusFirstTooth();
          break;

        case "End":
          event.preventDefault();
          focusLastTooth();
          break;

        case "Escape":
          event.preventDefault();
          returnToPreviousFocus();
          break;
      }
    },
    [announce, t]
  );

  // Enfocar el siguiente diente
  const focusNextTooth = useCallback((currentTooth, wrap = true) => {
    const toothNumbers = getAllToothNumbers();
    const currentIndex = toothNumbers.indexOf(currentTooth);

    let nextIndex = currentIndex + 1;
    if (nextIndex >= toothNumbers.length) {
      nextIndex = wrap ? 0 : currentIndex;
    }

    const nextTooth = toothNumbers[nextIndex];
    focusTooth(nextTooth);
  }, []);

  // Enfocar el diente anterior
  const focusPreviousTooth = useCallback((currentTooth, wrap = true) => {
    const toothNumbers = getAllToothNumbers();
    const currentIndex = toothNumbers.indexOf(currentTooth);

    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = wrap ? toothNumbers.length - 1 : 0;
    }

    const prevTooth = toothNumbers[prevIndex];
    focusTooth(prevTooth);
  }, []);

  // Enfocar primer diente
  const focusFirstTooth = useCallback(() => {
    const toothNumbers = getAllToothNumbers();
    if (toothNumbers.length > 0) {
      focusTooth(toothNumbers[0]);
    }
  }, []);

  // Enfocar último diente
  const focusLastTooth = useCallback(() => {
    const toothNumbers = getAllToothNumbers();
    if (toothNumbers.length > 0) {
      focusTooth(toothNumbers[toothNumbers.length - 1]);
    }
  }, []);

  // Enfocar un diente específico
  const focusTooth = useCallback(
    (toothNumber) => {
      const toothElement = document.querySelector(
        `[data-tooth-number="${toothNumber}"]`
      );
      if (toothElement) {
        saveFocusHistory();
        toothElement.focus();
        announce(t(`Focused on tooth ${toothNumber}`));
      }
    },
    [announce, t]
  );

  // Obtener todos los números de dientes disponibles
  const getAllToothNumbers = useCallback(() => {
    const toothElements = document.querySelectorAll("[data-tooth-number]");
    return Array.from(toothElements)
      .map((el) => parseInt(el.getAttribute("data-tooth-number")))
      .filter((num) => !isNaN(num))
      .sort((a, b) => a - b);
  }, []);

  // Guardar historial de foco
  const saveFocusHistory = useCallback(() => {
    const activeElement = document.activeElement;
    if (activeElement && focusHistory.current.length < 10) {
      focusHistory.current.push(activeElement);
    }
  }, []);

  // Retornar al foco anterior
  const returnToPreviousFocus = useCallback(() => {
    const previousElement = focusHistory.current.pop();
    if (previousElement && previousElement.focus) {
      previousElement.focus();
      announce(t("Returned to previous element"));
    }
  }, [announce, t]);

  // Generar ARIA labels para dientes
  const getToothAriaLabel = useCallback(
    (toothNumber, status, position) => {
      const statusLabel = t(`dental_status_${status}`) || t(status);
      const positionLabel = position ? t(position) : "";

      return (
        t("tooth_aria_label", {
          number: toothNumber,
          status: statusLabel,
          position: positionLabel,
        }) ||
        `Tooth ${toothNumber}, status: ${statusLabel}${
          position ? `, position: ${positionLabel}` : ""
        }`
      );
    },
    [t]
  );

  // Generar descripción para lectores de pantalla
  const getToothAriaDescription = useCallback(
    (toothNumber, status, isInteractive = true) => {
      const instructions = isInteractive
        ? t("tooth_interaction_instructions") ||
          "Press Enter or Space to change status, use arrow keys to navigate"
        : "";

      return (
        t("tooth_aria_description", {
          number: toothNumber,
          status: t(`dental_status_${status}`) || t(status),
          instructions,
        }) ||
        `Tooth number ${toothNumber} with ${status} status. ${instructions}`
      );
    },
    [t]
  );

  // Atributos ARIA para componentes del odontograma
  const getDentalChartAriaProps = useCallback(
    () => ({
      role: "application",
      "aria-label": t("dental_chart_aria_label") || "Interactive dental chart",
      "aria-description":
        t("dental_chart_aria_description") ||
        "Use arrow keys to navigate between teeth, Enter or Space to change tooth status",
    }),
    [t]
  );

  // Atributos ARIA para cuadrantes
  const getQuadrantAriaProps = useCallback(
    (quadrantName, toothCount) => ({
      role: "group",
      "aria-label":
        t("quadrant_aria_label", { name: quadrantName, count: toothCount }) ||
        `${quadrantName} quadrant with ${toothCount} teeth`,
      "aria-describedby": "dental-navigation-instructions",
    }),
    [t]
  );

  // Atributos ARIA para dientes individuales
  const getToothAriaProps = useCallback(
    (toothNumber, status, position, isSelected = false) => ({
      role: "button",
      tabIndex: isSelected ? 0 : -1,
      "aria-label": getToothAriaLabel(toothNumber, status, position),
      "aria-describedby": `tooth-${toothNumber}-description`,
      "aria-pressed": status !== "healthy",
      "data-tooth-number": toothNumber,
      "aria-current": isSelected ? "true" : undefined,
    }),
    [getToothAriaLabel]
  );

  // Anunciar cambios de estado de dientes
  const announceToothStatusChange = useCallback(
    (toothNumber, oldStatus, newStatus) => {
      const message =
        t("tooth_status_changed", {
          number: toothNumber,
          oldStatus: t(`dental_status_${oldStatus}`) || oldStatus,
          newStatus: t(`dental_status_${newStatus}`) || newStatus,
        }) ||
        `Tooth ${toothNumber} status changed from ${oldStatus} to ${newStatus}`;

      announce(message, "assertive");
    },
    [announce, t]
  );

  // Anunciar estadísticas de salud dental
  const announceDentalStats = useCallback(
    (stats) => {
      if (!stats) return;

      const message =
        t("dental_stats_announcement", {
          total: stats.totalTeeth,
          healthy: stats.healthyCount,
          problems: stats.problemsCount,
          percentage: stats.healthPercentage,
        }) ||
        `Dental chart has ${stats.totalTeeth} teeth total. ${stats.healthyCount} healthy, ${stats.problemsCount} with problems. Overall health: ${stats.healthPercentage}%`;

      announce(message);
    },
    [announce, t]
  );

  // Configurar shortcuts de teclado globales
  const setupKeyboardShortcuts = useCallback(() => {
    const handleGlobalKeydown = (event) => {
      // Solo actuar si estamos en el módulo dental
      if (!document.querySelector("[data-dental-module]")) return;

      const { key, ctrlKey, altKey } = event;

      if (ctrlKey && altKey) {
        switch (key) {
          case "h":
            event.preventDefault();
            focusFirstTooth();
            announce(t("Navigated to first tooth"));
            break;

          case "e":
            event.preventDefault();
            focusLastTooth();
            announce(t("Navigated to last tooth"));
            break;

          case "s":
            event.preventDefault();
            const statsElement = document.querySelector("[data-dental-stats]");
            if (statsElement) {
              statsElement.focus();
              announce(t("Focused on dental statistics"));
            }
            break;
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeydown);
    return () => document.removeEventListener("keydown", handleGlobalKeydown);
  }, [focusFirstTooth, focusLastTooth, announce, t]);

  return {
    // Navegación
    handleToothKeyNavigation,
    focusNextTooth,
    focusPreviousTooth,
    focusFirstTooth,
    focusLastTooth,
    focusTooth,

    // Anuncios
    announce,
    announceToothStatusChange,
    announceDentalStats,

    // ARIA Props
    getDentalChartAriaProps,
    getQuadrantAriaProps,
    getToothAriaProps,
    getToothAriaLabel,
    getToothAriaDescription,

    // Configuración
    setupKeyboardShortcuts,

    // Utilidades
    getAllToothNumbers,
    saveFocusHistory,
    returnToPreviousFocus,
  };
}
